import { useHandler } from "@chatbotgang/etude/react/useHandler";

import { logError } from "@/features/logger";
import { interlude } from "@/interlude";

const SMTM_LOGIN_PATH = "/auth/login";

/**
 * Build the SMTM SSO entry URL for a target SMTM page.
 *
 * The access token interlude hands us is an Arioso-signed SSO token, not an
 * SMTM session: SMTM only accepts it at `/auth/login`, which verifies it,
 * sets its own session cookie and 302s to `next`. So we never open the target
 * page with the token directly; we always go through the login endpoint on
 * the target's origin and pass the target path (with its query and hash) as
 * `next`.
 */
export function buildSmtmLoginUrl(target: URL, accessToken: string): string {
  const loginUrl = new URL(SMTM_LOGIN_PATH, target.origin);
  loginUrl.searchParams.set("token", accessToken);
  loginUrl.searchParams.set(
    "next",
    `${target.pathname}${target.search}${target.hash}`,
  );
  return loginUrl.toString();
}

export function useSmtmNavigation() {
  const createAccessTokenMutation = interlude.billing.useCreateAccessToken();

  const navigateToSmtm = useHandler((targetUrl: string) => {
    // Parse before opening anything: a malformed target (e.g. SMTM_URL
    // without a scheme) would otherwise throw inside onSuccess and strand
    // the blank tab, since onError does not run for that.
    let target: URL;
    try {
      target = new URL(targetUrl);
    } catch (error) {
      logError(error);
      return;
    }

    // Open the tab synchronously (inside the click) so popup blockers allow
    // it, then navigate it once the token arrives.
    const newWindow = window.open("", "_blank");
    if (!newWindow) return;
    newWindow.opener = null;

    createAccessTokenMutation.mutate(undefined, {
      onSuccess: (data) => {
        if (newWindow.closed) return;
        newWindow.location.href = buildSmtmLoginUrl(target, data.accessToken);
      },
      onError: () => {
        newWindow.close();
      },
    });
  });

  return navigateToSmtm;
}
